<template>
  <BaseInput
    v-bind="props"
    :class="props.class"
    :rules="mergedRules"
    v-on="
      reEmit(emit, ['update:loading', 'validationError', 'update:modelValue'])
    "
  >
    <template #input="{ id, describedBy, errorId, listeners, displayInvalid }">
      <PInputText
        :id
        v-bind="$attrs"
        v-model="modelValue"
        :aria-describedby="describedBy"
        :aria-errormessage="errorId"
        :aria-labelledby="ariaLabelledby"
        :disabled
        fluid
        :invalid="displayInvalid"
        maxlength="255"
        :name
        :pt="assign({ root: { name } }, pt?.pcInput ?? {})"
        :required
        :size
        v-on="listeners"
        @focus="emit('focus', name)"
      />
    </template>
    <template #emailTypoSuggest>
      <PMessage
        :class="$style.suggestion"
        :pt="pt?.pcEmailTypoSuggest"
        severity="info"
      >
        {{ tr("common_fe.fields.email.did_you_mean") }}
        <SButton
          action-type="quaternary"
          :class="$style.suggestionButton"
          size="inline"
          @click="emit('update:modelValue', suggestion!)"
        >
          {{ suggestion }}
        </SButton>
      </PMessage>
    </template>
    <template v-for="(_index, slotName) in $slots" #[slotName]="data">
      <slot :name="slotName" v-bind="data" />
    </template>
  </BaseInput>
</template>

<script setup lang="ts">
import type {
  InputTextPassThroughOptions,
  MessagePassThroughOptions,
} from "primevue";

import { assign } from "radash";
import { computed, toRef } from "vue";

import type { LocaleMorpheus } from "@solvari/translations";

import { SButton } from "@solvari/common-fe";
import { useModel } from "@solvari/common-fe/composables";
import { reEmit } from "@solvari/common-fe/helpers";
import { email, maxLength } from "@solvari/common-fe/validation";
import { useI18nCommonFe } from "@solvari/translations";

import type {
  BaseInputEmits,
  BaseInputPassThroughOptions,
  BaseInputProps,
} from "@/molecules/baseInput.ts";

import PInputText from "@/atoms/PInputText.vue";
import PMessage from "@/atoms/PMessage.vue";
import { useEmailTypoCorrection } from "@/helpers/useEmailTypoCorrection.ts";
import BaseInput from "@/molecules/BaseInput.vue";

const props = defineProps<
  BaseInputProps<string | null | undefined> & {
    class?: string;
    pt?: BaseInputPassThroughOptions & {
      pcEmailTypoSuggest?: MessagePassThroughOptions;
      pcInput?: InputTextPassThroughOptions;
    };
    typoCorrection?: LocaleMorpheus;
  }
>();
const emit = defineEmits<
  BaseInputEmits &
    ((event: "focus", name: string) => void) &
    ((event: "update:modelValue", value: string | null | undefined) => void)
>();

const modelValue = useModel("modelValue", props, emit);

const { tr } = useI18nCommonFe();

const { typoSuggestRule, suggestion } = useEmailTypoCorrection(
  toRef(() => (props.typoCorrection ? props.modelValue : "")),
  toRef(() => props.typoCorrection ?? "nl-NL"),
);

const inferredRules = [typoSuggestRule(), email(), maxLength(255)];
const mergedRules = computed(() =>
  props.rules ? [...inferredRules, ...props.rules] : inferredRules,
);
</script>

<style module lang="postcss">
.suggestion {
  @apply mt-2;

  &Button {
    @apply ml-0;
  }
}
</style>
